import React, { useState } from 'react';
import Modal from '../common/Modal';
import './PDFQuoteReviewModal.css';
import './PDFQuoteReviewModal-additions.css';

const PDFQuoteReviewModal = ({ extractedData, onConfirm, onCancel }) => {
  const [selectedComponents, setSelectedComponents] = useState(
    extractedData.components.map((_, index) => index)
  );

  const toggleComponent = (index) => {
    setSelectedComponents(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleConfirm = () => {
    const componentsToAdd = extractedData.components.filter((_, index) => 
      selectedComponents.includes(index)
    );
    onConfirm(componentsToAdd, extractedData.metadata);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return '#10b981'; // green
    if (confidence >= 0.6) return '#f59e0b'; // orange
    return '#ef4444'; // red
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.8) return 'High';
    if (confidence >= 0.6) return 'Medium';
    return 'Low';
  };

  const footer = (
    <>
      <div className="footer-info">
        <span>{selectedComponents.length} of {extractedData.components.length} components selected</span>
      </div>
      <div className="footer-actions">
        <button className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={handleConfirm}
          disabled={selectedComponents.length === 0}
        >
          Add {selectedComponents.length} Component{selectedComponents.length !== 1 ? 's' : ''} to Quote
        </button>
      </div>
    </>
  );

  return (
    <Modal isOpen onClose={onCancel} title="✨ Review Extracted Quote Data" maxWidth="900px" className="pdf-review-modal" footer={footer}>
          {/* AI Processing Info */}
          <div className="ai-info-banner">
            <div className="ai-info-item">
              <span className="ai-info-label">📄 Pages:</span>
              <span className="ai-info-value">{extractedData.pdfInfo?.pages || 'N/A'}</span>
            </div>
            <div className="ai-info-item">
              <span className="ai-info-label">🤖 Model:</span>
              <span className="ai-info-value">{extractedData.aiInfo?.model || 'GPT-4'}</span>
            </div>
            <div className="ai-info-item">
              <span className="ai-info-label">⏱️ Time:</span>
              <span className="ai-info-value">
                {extractedData.aiInfo?.processingTime 
                  ? `${(extractedData.aiInfo.processingTime / 1000).toFixed(1)}s`
                  : 'N/A'}
              </span>
            </div>
            <div className="ai-info-item">
              <span className="ai-info-label">🎯 Components:</span>
              <span className="ai-info-value">{extractedData.components.length}</span>
            </div>
          </div>

          {/* Quote Metadata */}
          {extractedData.metadata && (
            <div className="quote-info-section">
              <h3>Quote Information</h3>
              <div className="metadata-grid">
                {extractedData.metadata.title && (
                  <div className="metadata-item full-width">
                    <span className="metadata-label">Title:</span>
                    <span className="metadata-value">{extractedData.metadata.title}</span>
                  </div>
                )}
                {extractedData.offerSubtotal && (
                  <div className="metadata-item">
                    <span className="metadata-label">Package Subtotal:</span>
                    <span className="metadata-value">
                      ${extractedData.offerSubtotal.toFixed(2)}
                    </span>
                  </div>
                )}
                {extractedData.metadata.totalPrice > 0 && (
                  <div className="metadata-item">
                    <span className="metadata-label">Total Price:</span>
                    <span className="metadata-value">
                      ${extractedData.metadata.totalPrice.toFixed(2)}
                    </span>
                  </div>
                )}
                {extractedData.metadata.depositAmount > 0 && (
                  <div className="metadata-item">
                    <span className="metadata-label">Deposit:</span>
                    <span className="metadata-value">
                      ${extractedData.metadata.depositAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                {extractedData.metadata.travelStartDate && (
                  <div className="metadata-item">
                    <span className="metadata-label">Travel Dates:</span>
                    <span className="metadata-value">
                      {extractedData.metadata.travelStartDate}
                      {extractedData.metadata.travelEndDate && 
                        ` to ${extractedData.metadata.travelEndDate}`}
                    </span>
                  </div>
                )}
                {(extractedData.metadata.adults || extractedData.metadata.children) && (
                  <div className="metadata-item">
                    <span className="metadata-label">Travelers:</span>
                    <span className="metadata-value">
                      {extractedData.metadata.adults || 0} Adult(s)
                      {extractedData.metadata.children > 0 && 
                        `, ${extractedData.metadata.children} Child(ren)`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Warnings */}
          {extractedData.warnings && extractedData.warnings.length > 0 && (
            <div className="warnings-section">
              <h4>⚠️ Warnings</h4>
              <ul>
                {extractedData.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Uncertainties */}
          {extractedData.uncertainties && extractedData.uncertainties.length > 0 && (
            <div className="uncertainties-section">
              <h4>🤔 Please Review</h4>
              <ul>
                {extractedData.uncertainties.map((uncertainty, index) => (
                  <li key={index}>{uncertainty}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Package Extras */}
          {extractedData.packageExtras && extractedData.packageExtras.length > 0 && (
            <div className="package-extras-section">
              <h3>📦 Package Extras & Add-ons</h3>
              <p className="section-description">
                These items were detected as part of the package. They'll be noted in the main component description.
              </p>
              <div className="extras-grid">
                {extractedData.packageExtras.map((extra, index) => (
                  <div key={index} className="extra-item">
                    <div className="extra-header">
                      <span className="extra-name">{extra.name}</span>
                      {extra.included ? (
                        <span className="extra-badge included">Included</span>
                      ) : (
                        <span className="extra-badge optional">${extra.price?.toFixed(2) || '0.00'}</span>
                      )}
                    </div>
                    {extra.description && (
                      <p className="extra-description">{extra.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Components */}
          <div className="components-section">
            <h3>Extracted Components</h3>
            <p className="section-description">
              Select the components you want to add to your quote. You can review and edit them after adding.
            </p>

            <div className="components-list">
              {extractedData.components.map((component, index) => (
                <div 
                  key={index} 
                  className={`component-card ${selectedComponents.includes(index) ? 'selected' : ''} ${component.isMainComponent ? 'main-component' : ''} ${component.linkedTo ? 'linked-component' : ''}`}
                  onClick={() => toggleComponent(index)}
                >
                  <div className="component-header">
                    <div className="component-checkbox">
                      <input 
                        type="checkbox" 
                        checked={selectedComponents.includes(index)}
                        onChange={() => toggleComponent(index)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="component-title-section">
                      <div className="component-title-row">
                        {component.isMainComponent && (
                          <span className="main-badge">Main Package</span>
                        )}
                        <h4>{component.typeName}</h4>
                      </div>
                      {component.data.title && (
                        <p className="component-subtitle">{component.data.title}</p>
                      )}
                      {component.linkedTo && (
                        <div className="component-relationship">
                          🔗 Part of package
                          {component.relationshipType && (
                            <span className="relationship-type"> • {component.relationshipType}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div 
                      className="confidence-badge"
                      style={{ 
                        backgroundColor: getConfidenceColor(component.confidence),
                        color: 'white'
                      }}
                    >
                      {getConfidenceLabel(component.confidence)}
                    </div>
                  </div>

                  <div className="component-details">
                    {/* Price Display */}
                    <div className="detail-row price-row">
                      <span className="detail-label">Price:</span>
                      <span className="detail-value">
                        {component.includedInPackage ? (
                          <span className="included-badge">Included in Package Price</span>
                        ) : component.data.price ? (
                          `$${component.data.price.toFixed(2)}`
                        ) : (
                          <span className="included-badge">Included in Package Price</span>
                        )}
                      </span>
                    </div>
                    
                    {/* Other Details */}
                    {Object.entries(component.data)
                      .filter(([key, value]) => value !== null && value !== '' && key !== 'title' && key !== 'price')
                      .slice(0, 5)
                      .map(([key, value]) => (
                        <div key={key} className="detail-row">
                          <span className="detail-label">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </span>
                          <span className="detail-value">
                            {typeof value === 'number' && key.toLowerCase().includes('price') 
                              ? `$${value.toFixed(2)}`
                              : String(value)}
                          </span>
                        </div>
                      ))}
                    {Object.keys(component.data).length > 7 && (
                      <div className="more-fields">
                        +{Object.keys(component.data).length - 6} more fields
                      </div>
                    )}
                  </div>

                  {component.notes && (
                    <div className="component-notes">
                      <strong>Note:</strong> {component.notes}
                    </div>
                  )}

                  {component.hasUncertainties && (
                    <div className="uncertainty-warning">
                      ⚠️ Low confidence - please review carefully
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
    </Modal>
  );
};

export default PDFQuoteReviewModal;
